import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, TextInput } from 'react-native';
import { useMutation } from '@tanstack/react-query';
import { webColors, webBorderRadius, webShadows } from '../../../../constants/webStyles';
import { forgotPassword } from '../../../../services/auth';

const ForgotPasswordView = ({ onShowLogin }) => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const forgotPasswordMutation = useMutation(
    (data) => forgotPassword(data),
    {
      onError: (err) => {
        setError(err.response?.data?.message || 'Failed to send reset email. Please try again.');
      },
      onSuccess: () => {
        setSubmitted(true);
        setError('');
      },
    }
  );

  const handleSubmit = () => {
    setError('');

    if (!email) {
      setError('Please enter your email address');
      return;
    }

    // Simple email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    forgotPasswordMutation.mutate({ email });
  };

  const isLoading = forgotPasswordMutation.isLoading;

  // Success state
  if (submitted) {
    return (
      <View style={styles.container}>
        <View style={styles.successContainer}>
          <View style={styles.successIcon}>
            <Text style={styles.successCheckmark}>✓</Text>
          </View>
          <Text style={styles.successTitle}>Email Sent!</Text>
          <Text style={styles.successMessage}>
            We've sent a password reset link to your email address. Please check your inbox and follow the instructions to reset your password.
          </Text>
          <TouchableOpacity
            style={styles.btnBackToLogin}
            onPress={onShowLogin}
          >
            <Text style={styles.btnBackToLoginText}>Back to Sign In</Text>
          </TouchableOpacity>
        </View>

        <View style={styles.footer}>
          <Text style={styles.supportEmail}>support@supermedic.health</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <TouchableOpacity style={styles.backBtn} onPress={onShowLogin}>
        <Text style={styles.backBtnArrow}>‹</Text>
        <Text style={styles.backBtnText}>Back</Text>
      </TouchableOpacity>

      <View style={styles.formHeader}>
        <Text style={styles.formIcon}>✉️</Text>
        <Text style={styles.formTitle}>Forgot Password</Text>
        <Text style={styles.formSubtitle}>
          Please enter your email address to{' '}
          <Text style={styles.pinkText}>reset your password.</Text>
        </Text>
      </View>

      {error ? (
        <View style={styles.errorBox}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      ) : null}

      <View style={styles.form}>
        <View style={styles.inputWrapper}>
          <Text style={styles.inputIcon}>✉️</Text>
          <TextInput
            style={styles.input}
            placeholder="Email Address"
            placeholderTextColor={webColors.textMuted}
            value={email}
            onChangeText={setEmail}
            keyboardType="email-address"
            autoCapitalize="none"
            editable={!isLoading}
          />
        </View>

        <TouchableOpacity
          style={[styles.btnSubmit, isLoading && styles.btnSubmitDisabled]}
          onPress={handleSubmit}
          disabled={isLoading}
        >
          <Text style={styles.btnSubmitText}>
            {isLoading ? 'Sending...' : 'Submit'}
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.footer}>
        <Text style={styles.supportEmail}>support@supermedic.health</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    paddingHorizontal: 20,
  },
  backBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 8,
    marginBottom: 32,
  },
  backBtnArrow: {
    fontSize: 24,
    fontWeight: '300',
    color: webColors.textPrimary,
  },
  backBtnText: {
    fontSize: 16,
    fontWeight: '600',
    color: webColors.textPrimary,
  },
  formHeader: {
    marginBottom: 32,
  },
  formIcon: {
    fontSize: 40,
    marginBottom: 16,
  },
  formTitle: {
    fontSize: 28,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 8,
  },
  formSubtitle: {
    fontSize: 16,
    color: '#6b7280',
    lineHeight: 24,
  },
  pinkText: {
    color: webColors.primary,
  },
  errorBox: {
    backgroundColor: webColors.errorLight,
    padding: 12,
    borderRadius: webBorderRadius.small,
    marginBottom: 16,
  },
  errorText: {
    color: webColors.error,
    fontSize: 14,
  },
  form: {
    gap: 16,
  },
  inputWrapper: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: 16,
    top: '50%',
    transform: [{ translateY: -9 }],
    fontSize: 18,
    zIndex: 1,
  },
  input: {
    backgroundColor: webColors.backgroundAlt,
    borderWidth: 2,
    borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    paddingVertical: 16,
    paddingLeft: 48,
    paddingRight: 16,
    fontSize: 15,
    color: webColors.textPrimary,
  },
  btnSubmit: {
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: webBorderRadius.medium,
    alignItems: 'center',
    marginTop: 8,
    ...webShadows.button,
  },
  btnSubmitDisabled: {
    opacity: 0.7,
  },
  btnSubmitText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  footer: {
    alignItems: 'center',
    paddingTop: 24,
    marginTop: 'auto',
    borderTopWidth: 1,
    borderTopColor: webColors.border,
  },
  supportEmail: {
    fontSize: 14,
    color: webColors.textMuted,
  },
  // Success state styles
  successContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  successIcon: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: '#22c55e',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 24,
    shadowColor: '#22c55e',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.3,
    shadowRadius: 25,
  },
  successCheckmark: {
    fontSize: 40,
    color: '#ffffff',
    fontWeight: '700',
  },
  successTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: webColors.textPrimary,
    marginBottom: 12,
  },
  successMessage: {
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 32,
    paddingHorizontal: 20,
  },
  btnBackToLogin: {
    backgroundColor: webColors.primary,
    paddingVertical: 16,
    paddingHorizontal: 48,
    borderRadius: webBorderRadius.medium,
    ...webShadows.button,
  },
  btnBackToLoginText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ForgotPasswordView;
